import * as client from "../../../client";
import { perClientWithLob } from "../../../client-features";
import { STATE_ABBREV_TO_STATE_NAME } from "../../../datasets";
import { formatAbbreviatedMoney } from "../Money";
import {
  ConstructionCodeEditor,
  Editor,
  MoneyEditor,
  OneLineStringEditor,
  SicCodeEditor,
  StateEditor,
  YesNoEditor,
} from "./CellEditors";
import { AgEditorName } from "./CellEditorsAg";
import {
  CONSTRUCTION_CODE_DICT,
  formatConstructionDisplay,
} from "./construction";
import {
  formatModifierValue,
  getModifierOptions,
  hasModifierMapping,
  makeModifierEditor,
} from "./secondary_modifiers";
import { SIC_CODES, formatSicDisplay } from "./sic";
import {
  KM_TO_MILES,
  INTEGRATION_NOT_RUN,
  INTEGRATION_FOREIGN,
  INTEGRATION_ZERO_TIV,
  INTEGRATION_NO_MATCH,
} from "./utils";
import { RawAddressWithIndices } from "./types";

export type CustomAction =
  | "open_edit_modal_occupancy"
  | "open_street_view_modal";

export type SovColumnProps = {
  getValue: (data: RawAddressWithIndices) => string | number | undefined | null;
  columnName: string;
  width: number;
  freezeColumn?: boolean;
  field?: keyof client.RawAddress;
  editor?: Editor;
  agEditor?: AgEditorName;
  agEditorParams?: Record<string, unknown>;
  customAction?: CustomAction;
  bulkEdit?: boolean;
  isIconValue?: boolean;
  showTooltipsWhenNotFlagged?: boolean;
  infoText?: string;
  requiredForValidAddress?: boolean;
  renderNumberWithCommas?: boolean;
  filterable?: boolean;
  collapsibleGroup?: string;
  showHeaderSum?: boolean;
};

const MAP_COLUMN: SovColumnProps = {
  getValue: (data) => {
    const hasCoords =
      data.lat &&
      data.long &&
      data.lat.trim() !== "" &&
      data.long.trim() !== "";
    return hasCoords ? "Has Lat/Long" : "Missing Lat/Long";
  },
  columnName: "Map",
  width: 48,
  isIconValue: true,
  customAction: "open_street_view_modal",
  field: "lat",
  freezeColumn: true,
  filterable: true,
};
const LOC_COLUMN: SovColumnProps = {
  getValue: (data) => data.premises_number,
  columnName: "Loc",
  width: 40,
  field: "premises_number",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  freezeColumn: true,
};
const BLDG_COLUMN: SovColumnProps = {
  getValue: (data) => data.building_number,
  columnName: "Bldg",
  width: 40,
  field: "building_number",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  freezeColumn: true,
};
const ADDRESS_COLUMN: SovColumnProps = {
  getValue: (data) => data.street_address,
  columnName: "Address",
  width: 200,
  field: "street_address",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  requiredForValidAddress: true,
  freezeColumn: true,
};
const CITY_COLUMN: SovColumnProps = {
  getValue: (data) => data.city,
  columnName: "City",
  width: 150,
  field: "city",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",

  bulkEdit: true,
  requiredForValidAddress: true,
  freezeColumn: true,
};
const STATE_OPTIONS = ["", ...Object.keys(STATE_ABBREV_TO_STATE_NAME)];
const STATE_COLUMN: SovColumnProps = {
  getValue: (data) => data.state,
  columnName: "State",
  width: 85,
  field: "state",
  editor: StateEditor,
  agEditor: "agFilterableSelectCellEditor",
  agEditorParams: {
    values: STATE_OPTIONS,
    filter: true,
  },
  bulkEdit: true,
  requiredForValidAddress: true,
  freezeColumn: true,
  filterable: true,
};
const ZIP_COLUMN: SovColumnProps = {
  getValue: (data) => data.zip,
  columnName: "Zip",
  width: 75,
  field: "zip",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  requiredForValidAddress: true,
  freezeColumn: true,
};
const COUNTRY_COLUMN: SovColumnProps = {
  getValue: (data) => data.country_code,
  columnName: "Country",
  width: 75,
  field: "country_code",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  requiredForValidAddress: true,
  freezeColumn: true,
  filterable: true,
};
const FLOOD_BROKER_COLUMN: SovColumnProps = {
  getValue: (data) => data.flood_zone,
  columnName: "Flood (Broker)",
  width: 125,
  field: "flood_zone",
  filterable: true,
  collapsibleGroup: "hazard",
};
const FLOOD_FEMA_COLUMN: SovColumnProps = {
  getValue: (data) => {
    const geo = data.__extras.integrationData?.geographic_enrichment;
    // undefined -> no integration. hyphen -> integration ran, nothing found
    if (!geo) return undefined;
    return geo.flood_zone ?? "-";
  },
  columnName: "Flood (FEMA)",
  width: 125,
  filterable: true,
  collapsibleGroup: "hazard",
};
const EQ_ZONE_COLUMN: SovColumnProps = {
  getValue: (data) => {
    const geo = data.__extras.integrationData?.geographic_enrichment;
    // undefined -> no integration. hyphen -> integration ran, nothing found
    if (!geo) return undefined;
    return geo.earthquake_zone ?? "-";
  },
  columnName: "EQ Zone",
  width: 75,
  filterable: true,
  collapsibleGroup: "hazard",
};
const DTC_COLUMN: SovColumnProps = {
  getValue: (data) => {
    const rms = data.__extras.integrationData?.rms;
    if (!rms) return INTEGRATION_NOT_RUN;
    if (rms.is_foreign_exposure) return INTEGRATION_FOREIGN;
    if (data.tiv === 0) return INTEGRATION_ZERO_TIV;
    const km = rms.location_detail?.locationWsDetail?.distToCoast;
    if (km == null) return INTEGRATION_NO_MATCH;
    const miles = km * KM_TO_MILES;
    const rounded = Math.round(miles * 10) / 10;
    if (rounded === 0 && miles > 0) return "<0.1";
    return String(rounded);
  },
  columnName: "DTC (mi)",
  width: 75,
  collapsibleGroup: "hazard",
};
const PERIL_SCORE_FIELDS: {
  field: keyof client.PerilScores;
  label: string;
}[] = [
  { field: "hurricane", label: "Hurricane" },
  { field: "storm_surge", label: "Storm Surge" },
  { field: "earthquake", label: "Earthquake" },
  { field: "flood", label: "Flood" },
  { field: "wildfire", label: "Wildfire" },
  { field: "wind", label: "Wind" },
  { field: "hail", label: "Hail" },
  { field: "tornado", label: "Tornado" },
  { field: "lightning", label: "Lightning" },
];
const PERIL_SCORE_COLUMNS: SovColumnProps[] = PERIL_SCORE_FIELDS.map(
  ({ field, label }) => ({
    getValue: (data) => {
      const score =
        data.__extras.integrationData?.hazard_hub?.peril_scores?.[field];
      if (score == null || score === "N/A") return undefined;
      return score;
    },
    columnName: label,
    width: 100,
    collapsibleGroup: "HazardHub Peril Scores",
    filterable: true,
  })
);

const HH_COPE_GROUP = "HH COPE";
const hhCope = (data: RawAddressWithIndices) =>
  data.__extras.integrationData?.hazard_hub;
const HH_COPE_COLUMNS: SovColumnProps[] = [
  // Construction (C)
  {
    getValue: (data) => hhCope(data)?.building_condition,
    columnName: "Building Condition",
    width: 135,
    collapsibleGroup: HH_COPE_GROUP,
  },
  {
    getValue: (data) => hhCope(data)?.basement,
    columnName: "Basement",
    width: 100,
    collapsibleGroup: HH_COPE_GROUP,
  },
  // Occupancy (O)
  {
    getValue: (data) => hhCope(data)?.occupancy?.use_code,
    columnName: "Use Code",
    width: 100,
    collapsibleGroup: HH_COPE_GROUP,
  },
  {
    getValue: (data) => hhCope(data)?.occupancy?.standardized_land_use_code,
    columnName: "Land Use Code",
    width: 120,
    collapsibleGroup: HH_COPE_GROUP,
  },
  {
    getValue: (data) => hhCope(data)?.occupancy?.number_of_units,
    columnName: "# Units",
    width: 80,
    renderNumberWithCommas: true,
    collapsibleGroup: HH_COPE_GROUP,
  },
  // Protection (P)
  {
    getValue: (data) => hhCope(data)?.protection?.fire_protection_grade,
    columnName: "Fire Prot. Grade",
    width: 130,
    collapsibleGroup: HH_COPE_GROUP,
  },
  {
    getValue: (data) => hhCope(data)?.protection?.fire_protection_class,
    columnName: "Fire Prot. Class",
    width: 130,
    collapsibleGroup: HH_COPE_GROUP,
  },
  {
    getValue: (data) => hhCope(data)?.protection?.hydrant_score,
    columnName: "Hydrant Score",
    width: 115,
    renderNumberWithCommas: true,
    collapsibleGroup: HH_COPE_GROUP,
  },
  {
    getValue: (data) => {
      const mi = hhCope(data)?.protection?.drive_distance_to_fire_station_miles;
      if (mi == null) return undefined;
      return Math.round(mi * 10) / 10;
    },
    columnName: "Dist. to Fire Stn (mi)",
    width: 170,
    renderNumberWithCommas: true,
    collapsibleGroup: HH_COPE_GROUP,
  },
  {
    getValue: (data) =>
      hhCope(data)?.protection?.drive_time_to_fire_station_minutes,
    columnName: "Time to Fire Stn (min)",
    width: 170,
    renderNumberWithCommas: true,
    collapsibleGroup: HH_COPE_GROUP,
  },
  {
    getValue: (data) => hhCope(data)?.protection?.fire_station_type,
    columnName: "Fire Stn Type",
    width: 120,
    collapsibleGroup: HH_COPE_GROUP,
  },
  // Exposure (E)
  {
    getValue: (data) => hhCope(data)?.exposure?.fema_flood_zone,
    columnName: "FEMA Flood (HH)",
    width: 125,
    collapsibleGroup: HH_COPE_GROUP,
  },
  {
    getValue: (data) => hhCope(data)?.exposure?.wind_pool_score,
    columnName: "Wind Pool Score",
    width: 125,
    renderNumberWithCommas: true,
    collapsibleGroup: HH_COPE_GROUP,
  },
];

const RMS_GU_AAL_BUCKETS: { perils: string[]; label: string }[] = [
  { perils: ["PWH", "PSH"], label: "Named Storm AAL" },
  { perils: ["PES"], label: "EQ AAL" },
  { perils: ["PFL"], label: "Flood AAL" },
  { perils: ["PWB"], label: "Wildfire AAL" },
  { perils: ["PWT"], label: "SCS AAL" },
];
const RMS_GU_AAL_COLUMNS: SovColumnProps[] = RMS_GU_AAL_BUCKETS.map(
  ({ perils, label }) => ({
    getValue: (data) => {
      const aals = data.__extras.integrationData?.rms?.aal_gu_by_peril;
      if (!aals) return undefined;
      let sum = 0;
      let hasAny = false;
      for (const p of perils) {
        const value = aals[p];
        if (value != null) {
          sum += value;
          hasAny = true;
        }
      }
      return hasAny ? sum : undefined;
    },
    columnName: label,
    width: 135,
    collapsibleGroup: "RMS GU AAL",
    showHeaderSum: true,
  })
);
// Deductible columns mirror the RMS AAL bucket structure. PSH is omitted
// because it shares the named_storm deductible function with PWH; we
// surface a single Named Storm column.
const DEDUCTIBLE_BUCKETS: { peril: string; label: string }[] = [
  { peril: "AOP", label: "AOP Ded" },
  { peril: "PWH", label: "Named Storm Ded" },
  { peril: "PES", label: "EQ Ded" },
  { peril: "PFL", label: "Flood Ded" },
  { peril: "PWB", label: "Wildfire Ded" },
  { peril: "PWT", label: "SCS Ded" },
];
const formatPercent = (fraction: number): string => {
  const pct = fraction * 100;
  if (pct >= 10) return `${pct.toFixed(0)}%`;
  return `${pct.toFixed(1).replace(/\.0$/, "")}%`;
};
const formatDeductibleSummary = (s: client.DeductibleSummary): string => {
  switch (s.kind) {
    case "site":
      return `${formatPercent(s.value)} site`;
    case "per_unit":
      return `${formatPercent(s.value)} per unit`;
    case "occurrence":
      return `${formatAbbreviatedMoney(s.value)} per occ`;
    case "aggregate":
      return `${formatAbbreviatedMoney(s.value)} agg`;
  }
};
const DEDUCTIBLE_COLUMNS: SovColumnProps[] = DEDUCTIBLE_BUCKETS.map(
  ({ peril, label }) => ({
    getValue: (data) => {
      const summary = data.__extras.deductibleSummary?.[peril];
      if (!summary) {
        return data.__extras.deductibleSummaryLoading ? "…" : undefined;
      }
      return formatDeductibleSummary(summary);
    },
    columnName: label,
    width: 135,
    collapsibleGroup: "Deductibles",
  })
);
// Limit columns mirror the deductible bucket structure.
const LIMIT_BUCKETS: { peril: string; label: string }[] = [
  { peril: "AOP", label: "AOP Limit" },
  { peril: "PWH", label: "Named Storm Limit" },
  { peril: "PES", label: "EQ Limit" },
  { peril: "PFL", label: "Flood Limit" },
  { peril: "PWB", label: "Wildfire Limit" },
  { peril: "PWT", label: "SCS Limit" },
];
const formatLimitSummary = (s: client.LimitSummary): string | undefined => {
  const parts: string[] = [];
  if (s.occurrence != null) {
    parts.push(`${formatAbbreviatedMoney(s.occurrence)} occ`);
  }
  if (s.aggregate != null) {
    parts.push(`${formatAbbreviatedMoney(s.aggregate)} agg`);
  }
  return parts.length > 0 ? parts.join(", ") : undefined;
};
const LIMIT_COLUMNS: SovColumnProps[] = LIMIT_BUCKETS.map(
  ({ peril, label }) => ({
    getValue: (data) => {
      const summary = data.__extras.limitSummary?.[peril];
      if (!summary) {
        return data.__extras.limitSummaryLoading ? "…" : undefined;
      }
      return formatLimitSummary(summary);
    },
    columnName: label,
    width: 175,
    collapsibleGroup: "Limits",
  })
);
type SecondaryModifierField = {
  field: keyof client.RawAddress;
  label: string;
  width?: number;
};
const buildSecondaryModifierColumns = (
  fields: SecondaryModifierField[],
  group: string
): SovColumnProps[] =>
  fields.map(({ field, label, width }) => {
    const mapped = hasModifierMapping(field);
    return {
      getValue: (data) => {
        const raw = data[field] as string | null | undefined;
        return mapped ? formatModifierValue(field, raw) : raw;
      },
      columnName: label,
      width: width ?? 140,
      field,
      editor: mapped ? makeModifierEditor(field) : OneLineStringEditor,
      agEditor: mapped ? "agFilterableSelectCellEditor" : "agTextCellEditor",
      agEditorParams: mapped
        ? { values: getModifierOptions(field), filter: true }
        : undefined,
      bulkEdit: true,
      filterable: true,
      collapsibleGroup: group,
    };
  });

const WS_SECONDARY_MODIFIERS_GROUP = "WS Secondary Modifiers";
const WS_SECONDARY_MODIFIER_FIELDS: SecondaryModifierField[] = [
  { field: "opening_protection", label: "Opening Protection", width: 160 },
  { field: "roof_geometry", label: "Roof Geometry", width: 135 },
  { field: "roof_anchor", label: "Roof Anchor", width: 120 },
  { field: "ws_cladding_type", label: "WS Cladding", width: 130 },
  { field: "roof_covering", label: "Roof Covering", width: 130 },
  { field: "roof_age_or_condition", label: "Roof Age/Condition", width: 150 },
  {
    field: "roof_sheathing_attachment",
    label: "Roof Sheathing Attach.",
    width: 175,
  },
  {
    field: "roof_equipment_hurricane_bracing",
    label: "Roof Equip. Bracing",
    width: 165,
  },
  {
    field: "ws_construction_quality",
    label: "WS Constr. Quality",
    width: 150,
  },
  {
    field: "flashing_and_coping_quality",
    label: "Flashing & Coping",
    width: 150,
  },
  {
    field: "frame_foundation_connection",
    label: "Frame-Foundation Conn.",
    width: 180,
  },
  {
    field: "commercial_appurtenant_structures",
    label: "Comm. Appurtenant",
    width: 160,
  },
  {
    field: "residential_appurtenant_structures",
    label: "Res. Appurtenant",
    width: 155,
  },
  { field: "ground_level_equipment", label: "Ground Lvl Equip", width: 150 },
  { field: "floor_type", label: "Floor Type", width: 120 },
  { field: "flood_protection", label: "Flood Protection", width: 150 },
  { field: "flood_missiles", label: "Flood Missiles", width: 140 },
  { field: "basement_protection", label: "Basement Protection", width: 165 },
  {
    field: "vertical_exposure_distribution",
    label: "Vert. Exposure Dist.",
    width: 165,
  },
];
const WS_SECONDARY_MODIFIER_COLUMNS = buildSecondaryModifierColumns(
  WS_SECONDARY_MODIFIER_FIELDS,
  WS_SECONDARY_MODIFIERS_GROUP
);

const EQ_SECONDARY_MODIFIERS_GROUP = "EQ Secondary Modifiers";
const EQ_SECONDARY_MODIFIER_FIELDS: SecondaryModifierField[] = [
  { field: "plan_irregularity", label: "Plan Irregularity", width: 150 },
  { field: "soft_story", label: "Soft Story", width: 110 },
  { field: "vertical_irregularity", label: "Vert. Irregularity", width: 150 },
  { field: "eq_cladding_type", label: "EQ Cladding", width: 130 },
  { field: "short_column", label: "Short Column", width: 130 },
  { field: "urm_chimney", label: "URM Chimney", width: 130 },
  { field: "urm_partition", label: "URM Partition", width: 135 },
  { field: "ornamentation", label: "Ornamentation", width: 140 },
  { field: "cripple_walls", label: "Cripple Walls", width: 135 },
  { field: "frame_bolted_down", label: "Frame Bolted Down", width: 165 },
  { field: "anchoring", label: "Anchoring (Tilt-up)", width: 160 },
  { field: "urm_retrofit", label: "URM Retrofit", width: 130 },
  { field: "structural_upgrade", label: "Structural Upgrade", width: 160 },
  {
    field: "engineered_foundation",
    label: "Engineered Foundation",
    width: 175,
  },
  { field: "equipment_eq_bracing", label: "Equip. EQ Bracing", width: 160 },
  {
    field: "eq_construction_quality",
    label: "EQ Constr. Quality",
    width: 150,
  },
  {
    field: "equipment_support_maintenance",
    label: "Equip. Support Maint.",
    width: 175,
  },
  { field: "pounding", label: "Pounding", width: 110 },
  { field: "base_isolation", label: "Base Isolation", width: 135 },
  { field: "sprinkler_type", label: "Sprinkler Type", width: 135 },
  { field: "eqsl_susceptibility", label: "EQSL Susceptibility", width: 165 },
];
const EQ_SECONDARY_MODIFIER_COLUMNS = buildSecondaryModifierColumns(
  EQ_SECONDARY_MODIFIER_FIELDS,
  EQ_SECONDARY_MODIFIERS_GROUP
);

const BI_PREPAREDNESS_COLUMN: SovColumnProps = {
  getValue: (data) =>
    formatModifierValue("bi_preparedness", data.bi_preparedness),
  columnName: "BI Preparedness",
  width: 175,
  field: "bi_preparedness",
  editor: makeModifierEditor("bi_preparedness"),
  agEditor: "agFilterableSelectCellEditor",
  agEditorParams: { values: getModifierOptions("bi_preparedness") },
  bulkEdit: true,
  filterable: true,
};
const BI_REDUNDANCY_COLUMN: SovColumnProps = {
  getValue: (data) => formatModifierValue("bi_redundancy", data.bi_redundancy),
  columnName: "BI Redundancy",
  width: 200,
  field: "bi_redundancy",
  editor: makeModifierEditor("bi_redundancy"),
  agEditor: "agFilterableSelectCellEditor",
  agEditorParams: { values: getModifierOptions("bi_redundancy") },
  bulkEdit: true,
  filterable: true,
};

const COUNTY_COLUMN: SovColumnProps = {
  getValue: (data) => data.county,
  columnName: "County",
  width: 100,
  field: "county",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  freezeColumn: true,
};
const ISO_CONSTRUCTION_OPTIONS = [
  { label: "(none)", value: "" },
  ...Object.entries(CONSTRUCTION_CODE_DICT.ISO ?? {}).map(([code, desc]) => ({
    label: `${code} (${desc})`,
    value: code,
  })),
];
const CONSTRUCTION_COLUMN: SovColumnProps = {
  getValue: (data) =>
    formatConstructionDisplay(
      data.construction_code_type,
      data.construction_code,
      data.construction_description
    ),
  width: 220,
  columnName: "Construction",
  field: "construction_code",
  editor: ConstructionCodeEditor,
  showTooltipsWhenNotFlagged: true,
  agEditor: "agFilterableSelectCellEditor",
  agEditorParams: { values: ISO_CONSTRUCTION_OPTIONS },
  bulkEdit: true,
  filterable: true,
};
const SIC_OPTIONS = [
  { label: "(none)", value: "" },
  ...Object.entries(SIC_CODES).map(([code, desc]) => ({
    label: `${code} (${desc})`,
    value: code,
  })),
];
const SIC_CODE_COLUMN: SovColumnProps = {
  getValue: (data) => formatSicDisplay(data.sic_code),
  columnName: "SIC Code",
  width: 280,
  field: "sic_code",
  editor: SicCodeEditor,
  showTooltipsWhenNotFlagged: true,
  agEditor: "agFilterableSelectCellEditor",
  agEditorParams: { values: SIC_OPTIONS, filter: true },
  bulkEdit: true,
  filterable: true,
};
const YEAR_BUILT_COLUMN: SovColumnProps = {
  getValue: (data) => data.year_built,
  columnName: "Year Built",
  width: 110,
  field: "year_built",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  filterable: true,
};
const STORIES_COLUMN: SovColumnProps = {
  getValue: (data) => data.number_of_stories,
  columnName: "Stories",
  width: 90,
  field: "number_of_stories",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  filterable: true,
};
const SQFT_COLUMN: SovColumnProps = {
  getValue: (data) => data.square_footage,
  columnName: "Sqft",
  width: 90,
  field: "square_footage",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  renderNumberWithCommas: true,
};
const ITV_SQUARE_FOOTAGE_COLUMN: SovColumnProps = {
  getValue: () => undefined,
  columnName: "ITV",
  width: 80,
};
const VALUE_360_COLUMN: SovColumnProps = {
  getValue: () => undefined,
  columnName: "360 Value",
  width: 125,
};
const getBuildingValueColumn = (columnName: string): SovColumnProps => {
  return {
    getValue: (data) =>
      data.building_value ? parseFloat(data.building_value) : undefined,
    columnName: columnName,
    width: 125,
    field: "building_value",
    editor: MoneyEditor,
    agEditor: "agNumberCellEditor",
    bulkEdit: true,
    requiredForValidAddress: true,
  };
};
const BPP_VALUE_COLUMN: SovColumnProps = {
  getValue: (data) => (data.bpp_value ? parseFloat(data.bpp_value) : undefined),
  columnName: "BPP",
  width: 125,
  field: "bpp_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
  requiredForValidAddress: true,
};
const BI_INCL_EE_VALUE_COLUMN: SovColumnProps = {
  getValue: (data) =>
    data.bi_incl_ee_value ? parseFloat(data.bi_incl_ee_value) : undefined,
  columnName: "BI Incl EE",
  width: 125,
  field: "bi_incl_ee_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
  requiredForValidAddress: true,
};
const BI_EXCL_EE_VALUE_COLUMN: SovColumnProps = {
  getValue: (data) =>
    data.bi_excl_ee_value ? parseFloat(data.bi_excl_ee_value) : undefined,
  columnName: "BI Excl EE",
  width: 125,
  field: "bi_excl_ee_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
  requiredForValidAddress: true,
};
const TB_VALUE_COLUMN: SovColumnProps = {
  getValue: (data) =>
    data.tenant_betterments_value
      ? parseFloat(data.tenant_betterments_value)
      : undefined,
  columnName: "Tenant Betterments",
  width: 180,
  field: "tenant_betterments_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
};
const FURNITURE_FIXTURES_VALUE_COLUMN: SovColumnProps = {
  getValue: (data) =>
    data.furniture_and_fixtures_value
      ? parseFloat(data.furniture_and_fixtures_value)
      : undefined,
  columnName: "FF",
  width: 100,
  field: "furniture_and_fixtures_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
};
const INVENTORY_COLUMN: SovColumnProps = {
  getValue: (data) =>
    data.inventory_value ? parseFloat(data.inventory_value) : undefined,
  columnName: "Inventory",
  width: 100,
  field: "inventory_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
};
const CONTRACTORS_EQUIPMENT_COLUMN: SovColumnProps = {
  getValue: (data) =>
    data.contractors_equipment_value
      ? parseFloat(data.contractors_equipment_value)
      : undefined,
  columnName: "Contractor's Equip",
  width: 180,
  field: "contractors_equipment_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
};
const OUTDOOR_PROP_AUTOS_COLUMN: SovColumnProps = {
  getValue: (data) =>
    data.outdoor_property_and_autos_value
      ? parseFloat(data.outdoor_property_and_autos_value)
      : undefined,
  columnName: "Outdoor Prop & Autos",
  width: 200,
  field: "outdoor_property_and_autos_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
};
const SIGNS_VALUE_COLUMN: SovColumnProps = {
  getValue: (data) =>
    data.signs_value ? parseFloat(data.signs_value) : undefined,
  columnName: "Signs",
  width: 100,
  field: "signs_value",
  editor: MoneyEditor,
  agEditor: "agNumberCellEditor",
  bulkEdit: true,
};
const getTivColumn = (
  isEditable: boolean,
  infoText: string | undefined
): SovColumnProps => {
  return {
    getValue: (data) => data.tiv,
    columnName: "TIV",
    width: 125,
    field: "tiv",
    editor: isEditable ? MoneyEditor : undefined,
    agEditor: isEditable ? "agNumberCellEditor" : undefined,
    infoText: infoText,
    bulkEdit: isEditable,
    requiredForValidAddress: true,
  };
};
const TIV_PER_BUILDING_COLUMN: SovColumnProps = {
  getValue: (data) => {
    const n = data.number_of_buildings && parseFloat(data.number_of_buildings);
    if (n && !isNaN(n) && n > 0) {
      return data.tiv / n;
    }
    return data.tiv;
  },
  columnName: "TIV per Bldg",
  width: 125,
};
const SPRINKLER_COLUMN: SovColumnProps = {
  getValue: (data) => data.sprinkler,
  columnName: "Sprinkler",
  width: 90,
  field: "sprinkler",
  showTooltipsWhenNotFlagged: true,
  editor: YesNoEditor,
  agEditor: "agFilterableSelectCellEditor",
  agEditorParams: { values: ["", "Yes", "No"] },
  bulkEdit: true,
};
const FIRE_ALARM_COLUMN: SovColumnProps = {
  getValue: (data) => data.fire_alarm_type,
  columnName: "Fire Alarm",
  width: 110,
  field: "fire_alarm_type",
  showTooltipsWhenNotFlagged: true,
  editor: YesNoEditor,
  agEditor: "agFilterableSelectCellEditor",
  agEditorParams: { values: ["", "Yes", "No"] },
  bulkEdit: true,
};
const BURGLAR_ALARM_COLUMN: SovColumnProps = {
  getValue: (data) => data.burglar_alarm_type,
  columnName: "Burglar Alarm",
  width: 130,
  field: "burglar_alarm_type",
  showTooltipsWhenNotFlagged: true,
  editor: YesNoEditor,
  agEditor: "agFilterableSelectCellEditor",
  agEditorParams: { values: ["", "Yes", "No"] },
  bulkEdit: true,
};
const ROOFING_COLUMN: SovColumnProps = {
  getValue: (data) => data.roofing_year,
  columnName: "Roofing",
  width: 100,
  field: "roofing_year",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
};
const HEATING_COLUMN: SovColumnProps = {
  getValue: (data) => data.heating_year,
  columnName: "Heating",
  width: 100,
  field: "heating_year",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
};
const WIRING_COLUMN: SovColumnProps = {
  getValue: (data) => data.wiring_year,
  columnName: "Wiring",
  width: 100,
  field: "wiring_year",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
};
const PLUMBING_COLUMN: SovColumnProps = {
  getValue: (data) => data.plumbing_year,
  columnName: "Plumbing",
  width: 100,
  field: "plumbing_year",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
};
const NUMBER_OF_BUILDINGS_COLUMN: SovColumnProps = {
  getValue: (data) => data.number_of_buildings,
  columnName: "# of Buildings",
  width: 110,
  field: "number_of_buildings",
  editor: OneLineStringEditor,
  agEditor: "agTextCellEditor",
  bulkEdit: true,
  filterable: true,
};

const BASE_COLUMNS_1 = [MAP_COLUMN, LOC_COLUMN, BLDG_COLUMN];

const LOCATION_COLUMNS = [
  ADDRESS_COLUMN,
  CITY_COLUMN,
  STATE_COLUMN,
  ZIP_COLUMN,
  COUNTY_COLUMN,
  COUNTRY_COLUMN,
  FLOOD_BROKER_COLUMN,
  FLOOD_FEMA_COLUMN,
  EQ_ZONE_COLUMN,
  DTC_COLUMN,
];

const BASE_COLUMNS_2 = [
  CONSTRUCTION_COLUMN,
  SIC_CODE_COLUMN,
  YEAR_BUILT_COLUMN,
  NUMBER_OF_BUILDINGS_COLUMN,
  STORIES_COLUMN,
  SPRINKLER_COLUMN,
  ...RMS_GU_AAL_COLUMNS,
  ...DEDUCTIBLE_COLUMNS,
  ...LIMIT_COLUMNS,
  ...PERIL_SCORE_COLUMNS,
  ...HH_COPE_COLUMNS,
  ...WS_SECONDARY_MODIFIER_COLUMNS,
  ...EQ_SECONDARY_MODIFIER_COLUMNS,
];

const BASE_COLUMNS_3 = ({
  tivColEditable = true,
  tivInfoText,
  whCol,
  itvMultiplierCol,
  itvValueCol,
  tbCol,
  signsCol,
  ffCol,
  invCol,
  ceCol,
  opaCol,
}: {
  tivColEditable?: boolean;
  tivInfoText?: string;
  whCol?: SovColumnProps;
  itvMultiplierCol?: SovColumnProps;
  itvValueCol?: SovColumnProps;
  tbCol?: SovColumnProps;
  signsCol?: SovColumnProps;
  ffCol?: SovColumnProps;
  invCol?: SovColumnProps;
  ceCol?: SovColumnProps;
  opaCol?: SovColumnProps;
}) => [
  ...(itvMultiplierCol ? [itvMultiplierCol] : []),
  ...(itvValueCol ? [itvValueCol] : []),
  getTivColumn(tivColEditable, tivInfoText),
  getBuildingValueColumn("Building"),
  BPP_VALUE_COLUMN,
  BI_INCL_EE_VALUE_COLUMN,
  BI_EXCL_EE_VALUE_COLUMN,
  ...(tbCol ? [tbCol] : []),
  ...(signsCol ? [signsCol] : []),
  ...(ffCol ? [ffCol] : []),
  ...(invCol ? [invCol] : []),
  ...(ceCol ? [ceCol] : []),
  ...(opaCol ? [opaCol] : []),
  ...(whCol ? [whCol] : []),
];

const BASE_COLUMNS_4 = [
  ROOFING_COLUMN,
  HEATING_COLUMN,
  WIRING_COLUMN,
  PLUMBING_COLUMN,
];

export const getAddressColumns = perClientWithLob<SovColumnProps[]>({
  pantheon: () => [
    ...BASE_COLUMNS_1,
    ...LOCATION_COLUMNS,
    ...BASE_COLUMNS_2,
    SQFT_COLUMN,
    VALUE_360_COLUMN,
    ...BASE_COLUMNS_3({
      tivColEditable: false,
      tbCol: TB_VALUE_COLUMN,
      signsCol: SIGNS_VALUE_COLUMN,
      ffCol: FURNITURE_FIXTURES_VALUE_COLUMN,
      invCol: INVENTORY_COLUMN,
      ceCol: CONTRACTORS_EQUIPMENT_COLUMN,
      opaCol: OUTDOOR_PROP_AUTOS_COLUMN,
    }),
    FIRE_ALARM_COLUMN,
    BURGLAR_ALARM_COLUMN,
    ...BASE_COLUMNS_4,
    BI_PREPAREDNESS_COLUMN,
    BI_REDUNDANCY_COLUMN,
  ],
  luminor: () => [
    ...BASE_COLUMNS_1,
    ...LOCATION_COLUMNS,
    ...BASE_COLUMNS_2,
    ITV_SQUARE_FOOTAGE_COLUMN,
    TIV_PER_BUILDING_COLUMN,
    ...BASE_COLUMNS_3({
      tivColEditable: false,
      tbCol: TB_VALUE_COLUMN,
      signsCol: SIGNS_VALUE_COLUMN,
      ffCol: FURNITURE_FIXTURES_VALUE_COLUMN,
      invCol: INVENTORY_COLUMN,
      ceCol: CONTRACTORS_EQUIPMENT_COLUMN,
      opaCol: OUTDOOR_PROP_AUTOS_COLUMN,
    }),
    ...BASE_COLUMNS_4,
    SQFT_COLUMN,
    BI_PREPAREDNESS_COLUMN,
    BI_REDUNDANCY_COLUMN,
  ],
});
